import React from "react";

// Custom SVG arc gauge 0-100 with red/yellow/green zones
export default function GaugeMeter({ value = 50, label = "Neutral", size = 320 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const cx = size / 2;
  const cy = size / 2 + 20;
  const r = size * 0.4;
  const startAngle = -210; // degrees
  const endAngle = 30;
  const total = endAngle - startAngle;

  const polar = (angle) => {
    const a = (angle * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  const arcPath = (a1, a2, large = 0) => {
    const [x1, y1] = polar(a1);
    const [x2, y2] = polar(a2);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  // zone boundaries
  const z1 = startAngle + (30 / 100) * total; // bear -> neutral
  const z2 = startAngle + (70 / 100) * total; // neutral -> bull
  const needleAngle = startAngle + (v / 100) * total;
  const [nx, ny] = polar(needleAngle);

  const colorFor = v < 30 ? "#FF3B30" : v < 70 ? "#FFCC00" : "#34C759";

  return (
    <div className="flex flex-col items-center" data-testid="gauge-meter">
      <svg width={size} height={size * 0.75} viewBox={`0 0 ${size} ${size * 0.85}`}>
        {/* Track */}
        <path d={arcPath(startAngle, endAngle)} stroke="#1A1A1A" strokeWidth="22" fill="none" strokeLinecap="butt" />
        {/* Bear zone */}
        <path d={arcPath(startAngle, z1)} stroke="#FF3B30" strokeWidth="22" fill="none" strokeLinecap="butt" opacity="0.85" />
        {/* Neutral zone */}
        <path d={arcPath(z1, z2)} stroke="#FFCC00" strokeWidth="22" fill="none" strokeLinecap="butt" opacity="0.85" />
        {/* Bull zone */}
        <path d={arcPath(z2, endAngle)} stroke="#34C759" strokeWidth="22" fill="none" strokeLinecap="butt" opacity="0.85" />
        {/* Tick marks */}
        {[0, 25, 50, 75, 100].map((t) => {
          const a = startAngle + (t / 100) * total;
          const [x1, y1] = polar(a);
          const ar = (a * Math.PI) / 180;
          const x2 = cx + (r - 30) * Math.cos(ar);
          const y2 = cy + (r - 30) * Math.sin(ar);
          const lx = cx + (r - 50) * Math.cos(ar);
          const ly = cy + (r - 50) * Math.sin(ar);
          return (
            <g key={t}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#A1A1AA" strokeWidth="1.5" />
              <text x={lx} y={ly} fontSize="10" fill="#A1A1AA" textAnchor="middle" dominantBaseline="middle" fontFamily="IBM Plex Mono">{t}</text>
            </g>
          );
        })}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={colorFor} strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="8" fill={colorFor} />
        <circle cx={cx} cy={cy} r="4" fill="#0A0A0A" />
        {/* Center value */}
        <text x={cx} y={cy + 50} fontSize="56" fill="#FFFFFF" textAnchor="middle" fontFamily="Chivo" fontWeight="700" data-testid="gauge-value">
          {Math.round(v)}
        </text>
        <text x={cx} y={cy + 78} fontSize="13" fill={colorFor} textAnchor="middle" fontFamily="IBM Plex Mono" letterSpacing="0.25em" data-testid="gauge-label">
          {label.toUpperCase()}
        </text>
      </svg>
    </div>
  );
}
